import React, { useCallback, useMemo, useState } from 'react';
import { FormErrors, SubmissionError } from 'redux-form';
import styled from 'styled-components/macro';
import { PurchaseGalleryDialog } from '../PurchaseGallery/PurchaseGalleryDialog';
import { MoviePurchaseOptionsModal } from 'src/client/components/modals/MoviePurchaseOptionsModal';
import { secondsToRuntime } from 'src/client/utils/converters';
import { MovieRegularPurchasePanel } from 'src/client/components/panels/MovieRegularPurchasePanel';
import { MovieStaffSelectionPurchasePanel } from 'src/client/components/panels/MovieStaffSelectionPurchasePanel';
import { useSelector } from 'src/client/redux';
import { MetartIntimatePurchasePanel } from 'src/client/components/panels/MetartIntimatePurchasePanel';
import { Movie } from 'src/@types/Movie';
import { PurchaseOption } from '../../components/dropdowns/DownloadDropdown/PurchaseDownloadDropdownMenu';
import { ResponseError } from 'superagent';
import { useGetGalleryCoverUrl } from 'src/client/helpers/urls/useGetGalleryCoverUrl';
import { useGalleryPrices } from './useGalleryPrices';
import { usePurchaseGallery } from 'src/client/requests/usePurchaseGallery';
import { CurrencySymbols } from 'src/shared/constants/currency';

const QualityMap = {
  UHD: '4k',
  HD: '1080p',
  SD: '720p',
};

type Props = {
  className?: string;
  gallery: Movie;
};

export default function MoviePurchasePanel(props: Props) {
  const { gallery } = props;

  const user = useSelector((state) => state.auth.user);
  const {
    config,
    contentCdnUrl,
    config: { hasPPS, has4kResolution },
  } = useSelector((state) => state.app);

  const getGalleryCover = useGetGalleryCoverUrl();
  const { products } = useGalleryPrices(gallery.isStaffSelection ? 'selection' : 'film');
  const purchaseMutation = usePurchaseGallery();

  const [purchaseModalSettings, setPurchaseModalSettings] = useState<
    | false
    | {
        currency: string;
        price: number;
        quality: string;
        title: string;
        vatPct: number;
      }
  >(false);
  const [showPurchaseOptionsModal, setShowPurchaseOptionsModal] = useState(false);

  const priceOptions = useMemo(
    () =>
      Object.keys(QualityMap).reduce((acc, quality) => {
        const product = products.find((product) => product.qualityType.toUpperCase() === quality.toUpperCase());

        if (product) {
          const option = QualityMap[quality];

          acc[option] = {
            currency: product.currency,
            price: parseFloat(product.initialRegionalPriceWithoutVat),
            quality,
            title: `${option} ${quality}`,
            vatPct: parseFloat(product.vatPct),
          };
        }

        return acc;
      }, {}),
    [products],
  );

  const purchaseOptions = useMemo(
    () =>
      gallery.files.sizes.videos
        .filter((purchaseOption) => !!priceOptions[purchaseOption.id])
        .map((download) => ({
          ...priceOptions[download.id],
          id: download.id,
          size: download.size,
        })),
    [gallery.files.sizes.videos, priceOptions],
  );

  const purchaseScene = useCallback(
    async ({ email, password }) => {
      if (!purchaseModalSettings) {
        if (!email) {
          return;
        }

        throw new SubmissionError({ email: 'Invalid Purchase Option' } as FormErrors<{ email: string }>);
      }

      const data = {
        email,
        galleryUUID: gallery.UUID,
        password,
        quality: purchaseModalSettings.quality,
      };

      try {
        await purchaseMutation.mutateAsync(data);
      } catch (err) {
        if (!email) {
          return;
        }

        const error = err as ResponseError;
        let message = 'Submission failed, please try again later';

        if (error?.response?.status === 409) {
          message = 'Please login to complete this purchase';
        }

        throw new SubmissionError({ email: message } as FormErrors<{
          email: string;
        }>);
      }
    },
    [gallery.UUID, purchaseModalSettings, purchaseMutation],
  );

  const purchase = useCallback((purchaseItem: PurchaseOption) => {
    setShowPurchaseOptionsModal(false);

    setPurchaseModalSettings(purchaseItem);
  }, []);

  const sortedPrices = useMemo(() => purchaseOptions.sort((a, b) => a.price - b.price), [purchaseOptions]);

  const purchasePanel = useMemo(() => {
    if (gallery.isStaffSelection) {
      return <MovieStaffSelectionPurchasePanel onPurchase={() => setShowPurchaseOptionsModal(true)} />;
    }

    if (gallery.isIntimateSelection) {
      return <MetartIntimatePurchasePanel onPurchase={() => setShowPurchaseOptionsModal(true)} />;
    }

    const [startingProduct] = sortedPrices;
    const currencySymbol = CurrencySymbols[startingProduct?.currency || CurrencySymbols.USD];
    const price = startingProduct?.price ?? 0;

    return (
      <MovieRegularPurchasePanel
        downloadsDisabled={gallery.downloadsDisabled || purchaseOptions.length === 0}
        galleryCount={config.galleryCount}
        movieCount={config.movieCount}
        onPurchase={() => setShowPurchaseOptionsModal(true)}
        startingPrice={`${currencySymbol}${price}`}
        hasPPS={hasPPS}
        has4kResolution={has4kResolution}
      />
    );
  }, [
    config.galleryCount,
    config.movieCount,
    gallery.downloadsDisabled,
    gallery.isIntimateSelection,
    gallery.isStaffSelection,
    has4kResolution,
    hasPPS,
    purchaseOptions.length,
    sortedPrices,
  ]);

  return (
    <PanelWrapper className={props.className}>
      {purchasePanel}

      <MoviePurchaseOptionsModal
        purchaseOptions={purchaseOptions}
        show={showPurchaseOptionsModal}
        onHide={() => setShowPurchaseOptionsModal(false)}
        onSelect={purchase}
      />

      <PurchaseGalleryDialog
        currency={purchaseModalSettings ? purchaseModalSettings.currency : '$'}
        description={gallery.description}
        onHide={() => setPurchaseModalSettings(false)}
        price={purchaseModalSettings ? purchaseModalSettings.price : 0}
        purchaseError={purchaseMutation.error}
        purchaseLoading={purchaseMutation.isLoading}
        purchaseRedirect={purchaseMutation.data?.redirectURL}
        purchaseScene={purchaseScene}
        purchaseStatus={purchaseMutation.isSuccess}
        quality={purchaseModalSettings ? purchaseModalSettings.title : ''}
        runtime={secondsToRuntime(gallery.media.runtime)}
        show={!!purchaseModalSettings}
        submitted={!purchaseMutation.isIdle}
        thumbnail={getGalleryCover(contentCdnUrl, gallery)}
        title={gallery.name}
        type="movie"
        user={!!(user && user.email)}
        vatPct={purchaseModalSettings ? purchaseModalSettings.vatPct : 0}
        wideThumbnail={getGalleryCover(contentCdnUrl, gallery, { wide: true })}
        year={new Date(gallery.publishedAt).getUTCFullYear()}
      />
    </PanelWrapper>
  );
}

const PanelWrapper = styled.div`
  & > div {
    display: flex;
    flex-direction: column;
  }
`;
